// The metric registry — one definition of load, referenced by key (ADR-0015).
//
// Until 2026-08-08 there were THREE ways to say "how much work is there today":
// the activity's own `demand_formula`, the staffing profile's identically-shaped
// `demand_formula`, and the work-content person-minutes. Two of them disagreed
// about the same room (a recouche was ½ an à blanc in one and ⅓ in the other),
// and both were live on different screens.
//
// This module is the functional core of the fix (ADR-0012: types + pure
// functions, no I/O, no clock, no invariant enforcement). Storage is
// `board_metric`; referential integrity and cycle-freeness are enforced by
// trigger in the same transaction that writes them. The guard here is a SECOND
// line that reports rather than throws — a half-finished registry must not take
// down the board.

import type { BoardDay } from './boardCoverage.js';

// ── Definitions ─────────────────────────────────────────────────────────────

/**
 * What a metric's number MEANS. Carried because the badge renders it to staff
 * and « 11 » with no unit was exactly the defect ADR-0015 set out to fix — it
 * was neither people, nor rooms, nor minutes.
 */
export type MetricUnit = 'count' | 'personMinutes' | 'equivalents' | 'heads';

export type MetricKind = 'base' | 'formula' | 'profileOutput';

/** The outputs a work-content staffing profile publishes (brief §4.2). */
export type ProfileOutputName = 'workMinutes' | 'aBlancEquivalents' | 'heads';

export const PROFILE_OUTPUT_NAMES: readonly ProfileOutputName[] = [
  'workMinutes',
  'aBlancEquivalents',
  'heads',
] as const;

/** Unit each published profile output is measured in — fixed by what it IS. */
export const PROFILE_OUTPUT_UNITS: Record<ProfileOutputName, MetricUnit> = {
  workMinutes: 'personMinutes',
  aBlancEquivalents: 'equivalents',
  heads: 'heads',
};

/**
 * The `board_days` fields a `base` metric may read. Deliberately code-anchored:
 * the evaluator has to know how to read a stored field, so letting the UI invent
 * base metrics would produce rows nothing can resolve (ADR-0015, rejected
 * alternatives).
 */
export const BASE_METRIC_FIELDS = [
  'arrivals',
  'stayovers',
  'departures',
  'occupancy',
  'breakfasts',
  'halfBoards',
  'diners',
  'covers',
  // 0129 (M3) — the people dimension. Feed-owned and NULL until M4 writes them,
  // so the four metric rows that read these ship INACTIVE: a base metric over an
  // empty column renders a confident 0 on the demand row.
  'guestsAdults',
  'guestsChildren',
  'arrivalsGuests',
  'departuresGuests',
] as const;

export type BaseMetricField = (typeof BASE_METRIC_FIELDS)[number];

export function isBaseMetricField(value: string): value is BaseMetricField {
  return (BASE_METRIC_FIELDS as readonly string[]).includes(value);
}

/**
 * How a metric's value is presented once evaluated. Base counts are whole
 * things and round up (half a room is still a room to clean — the rule
 * `evaluateFormula` has always applied); equivalents and minutes are genuinely
 * fractional and must NOT be rounded, or « 7,3 à-blancs éq. » silently becomes
 * « 8 » and the comfort-floor threshold stops meaning what it says.
 */
export type MetricRounding = 'ceil' | 'none';

export interface MetricDef {
  /** Immutable camelCase identifier. Formulas reference this, so it never changes. */
  key: string;
  /** Staff-visible French name — « À blanc équivalent ». Editable. */
  labelFr: string;
  unit: MetricUnit;
  kind: MetricKind;
  rounding: MetricRounding;
  /** `base` only — which `BoardDay` field to read. */
  sourceField: BaseMetricField | null;
  /** `formula` only — weighted sum over OTHER metric keys (absent key = 0). */
  formula: Record<string, number> | null;
  /** `profileOutput` only — which staffing profile publishes it. */
  sourceProfileId: number | null;
  /** `profileOutput` only — which of that profile's outputs. */
  sourceOutput: ProfileOutputName | null;
  active: boolean;
  sort: number;
  /**
   * Headline figure for a day — rendered by every per-day summary (the Mois
   * cell, the Semaine derived row, the Jour derived cell). A property of the
   * METRIC rather than of a view, which is why one flag serves all three and
   * any later surface for free.
   */
  inSummary: boolean;
}

/** Keyed by `key`. Built once per load; every lookup below is O(1). */
export type MetricRegistry = ReadonlyMap<string, MetricDef>;

export function buildRegistry(defs: readonly MetricDef[]): MetricRegistry {
  return new Map(defs.map((d) => [d.key, d]));
}

// ── Evaluation context ──────────────────────────────────────────────────────

/** One work-content profile's resolved outputs for the day being evaluated. */
export interface ProfileOutputs {
  workMinutes: number;
  aBlancEquivalents: number;
  heads: number;
}

/**
 * Everything an evaluation needs. `profileOutputs` is supplied by the caller
 * (the composition root resolves the staffing specs first) rather than computed
 * here — this module must not depend on the staffing model, or the two would be
 * mutually recursive at the module level for no benefit.
 */
export interface MetricContext {
  day: BoardDay | null;
  /** By `board_staffing_profile.id`. Missing profile = its outputs are unknown. */
  profileOutputs?: ReadonlyMap<number, ProfileOutputs>;
}

/** Why a metric could not be evaluated. Surfaced in the editor, never thrown. */
export interface MetricProblem {
  key: string;
  reason: string;
}

export interface MetricResult {
  /** Null when the metric could not be resolved — NOT 0. See `evaluateMetric`. */
  value: number | null;
  problems: MetricProblem[];
}

/** Read one stored day field. Unseeded occupancy reads 0, as it always has. */
function baseValue(day: BoardDay, field: BaseMetricField): number {
  switch (field) {
    case 'arrivals':
      return day.arrivals;
    case 'stayovers':
      return day.stayovers;
    case 'departures':
      return day.departures;
    case 'occupancy':
      return day.occupancy ?? 0;
    case 'breakfasts':
      return day.breakfasts;
    case 'halfBoards':
      return day.halfBoards;
    case 'diners':
      return day.diners;
    case 'covers':
      return day.covers;
    // `?? 0` for the same reason `occupancy` does it: the evaluator's contract
    // is a number. The null survives where it matters — in the column, in the
    // row type, and in anything that renders "not yet imported" rather than
    // "zero guests".
    case 'guestsAdults':
      return day.guestsAdults ?? 0;
    case 'guestsChildren':
      return day.guestsChildren ?? 0;
    case 'arrivalsGuests':
      return day.arrivalsGuests ?? 0;
    case 'departuresGuests':
      return day.departuresGuests ?? 0;
  }
}

function applyRounding(value: number, rounding: MetricRounding): number {
  // Clamped at 0 in both cases: a negative coefficient is legal (« couverts
  // moins demi-pensions ») but negative load is not.
  const clamped = Math.max(0, value);
  return rounding === 'ceil' ? Math.ceil(clamped) : clamped;
}

/**
 * Evaluate one metric for one day.
 *
 * **Null, not zero, when unresolvable.** This distinction is load-bearing and
 * was learned the expensive way elsewhere in this domain: seeding an unknown
 * figure with 0 makes the board report « pas de besoins » (a positive claim that
 * nothing is needed) instead of « pas encore saisi » (no data). The predictive
 * brief parks the whole PMS-forward-demand feature over exactly this. A caller
 * that genuinely wants 0 can coalesce; a caller that would have shown a false
 * "nothing to do" cannot un-see it.
 *
 * Cycle-safe: `visiting` carries the current DFS path, so a cycle the DB trigger
 * somehow admitted is reported rather than blowing the stack.
 */
export function evaluateMetric(
  registry: MetricRegistry,
  key: string,
  ctx: MetricContext,
  memo: Map<string, number | null> = new Map(),
  visiting: ReadonlySet<string> = new Set(),
  problems: MetricProblem[] = [],
): MetricResult {
  if (memo.has(key)) return { value: memo.get(key) ?? null, problems };

  if (visiting.has(key)) {
    problems.push({ key, reason: `cycle: ${[...visiting, key].join(' → ')}` });
    memo.set(key, null);
    return { value: null, problems };
  }

  const def = registry.get(key);
  if (!def) {
    problems.push({ key, reason: 'unknown metric' });
    return { value: null, problems };
  }
  if (!def.active) {
    problems.push({ key, reason: 'metric is inactive' });
    memo.set(key, null);
    return { value: null, problems };
  }

  let value: number | null = null;

  if (def.kind === 'base') {
    if (def.sourceField === null) {
      problems.push({ key, reason: 'base metric has no source field' });
    } else if (ctx.day === null) {
      // Not a problem — an unsaved day legitimately has no metrics.
      value = null;
    } else {
      value = applyRounding(baseValue(ctx.day, def.sourceField), def.rounding);
    }
  } else if (def.kind === 'profileOutput') {
    if (def.sourceProfileId === null || def.sourceOutput === null) {
      problems.push({ key, reason: 'profile-output metric has no source profile' });
    } else {
      const outputs = ctx.profileOutputs?.get(def.sourceProfileId);
      // Absent outputs mean the profile did not resolve for this day (no
      // workload saved yet). Unknown, not zero.
      value = outputs ? applyRounding(outputs[def.sourceOutput], def.rounding) : null;
    }
  } else {
    const formula = def.formula ?? {};
    const terms = Object.entries(formula).filter(([, c]) => typeof c === 'number' && c !== 0);
    if (terms.length === 0) {
      problems.push({ key, reason: 'formula metric has no terms' });
    } else {
      const nextVisiting = new Set(visiting).add(key);
      let total = 0;
      // An unresolvable TERM makes the whole sum unresolvable — averaging over
      // the terms that did resolve would silently understate the load.
      let resolved = true;
      for (const [refKey, coefficient] of terms) {
        const term = evaluateMetric(registry, refKey, ctx, memo, nextVisiting, problems);
        if (term.value === null) {
          resolved = false;
          continue;
        }
        total += (coefficient as number) * term.value;
      }
      value = resolved ? applyRounding(total, def.rounding) : null;
    }
  }

  memo.set(key, value);
  return { value, problems };
}

/** Evaluate several metrics sharing one memo — the board's per-day path. */
export function evaluateMetrics(
  registry: MetricRegistry,
  keys: readonly string[],
  ctx: MetricContext,
): { values: Map<string, number | null>; problems: MetricProblem[] } {
  const memo = new Map<string, number | null>();
  const problems: MetricProblem[] = [];
  const values = new Map<string, number | null>();
  for (const key of keys) {
    values.set(key, evaluateMetric(registry, key, ctx, memo, new Set(), problems).value);
  }
  return { values, problems };
}

// ── Presentation ────────────────────────────────────────────────────────────

/**
 * Short FR unit suffix for a badge. « éq. » and « pers. » are the abbreviations
 * already in use in `PrevisionnelStrip`, kept identical so the same quantity
 * never reads two ways on two surfaces.
 */
export const UNIT_SUFFIX_FR: Record<MetricUnit, string> = {
  count: '',
  personMinutes: 'min',
  equivalents: 'éq.',
  heads: 'pers.',
};

/** Whole numbers plain; fractional to one decimal, French-style (« 7,3 »). */
export function formatMetricValue(value: number, unit: MetricUnit): string {
  const fractional = unit === 'equivalents' && !Number.isInteger(value);
  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: fractional ? 1 : 0,
    maximumFractionDigits: fractional ? 1 : 0,
  });
}

/** « 7,3 à-blancs éq. » — the badge text. Null value renders nothing. */
export function formatMetric(def: MetricDef, value: number | null): string | null {
  if (value === null) return null;
  const suffix = UNIT_SUFFIX_FR[def.unit];
  return suffix === ''
    ? formatMetricValue(value, def.unit)
    : `${formatMetricValue(value, def.unit)} ${suffix}`;
}

/**
 * Human-readable formula, resolving referenced keys to their FR labels —
 * « Départs (à blancs) + ½ × Recouches ». An unknown key renders as its raw key
 * rather than being dropped, so a broken reference is visible in the editor
 * instead of silently vanishing from the displayed rule.
 */
export function formatMetricFormula(registry: MetricRegistry, formula: Record<string, number>): string {
  const terms = Object.entries(formula)
    .filter(([, c]) => typeof c === 'number' && c !== 0)
    .map(([key, c]) => {
      const label = registry.get(key)?.labelFr ?? key;
      if (c === 1) return label;
      if (c === 0.5) return `½ × ${label}`;
      return `${c.toLocaleString('fr-FR')} × ${label}`;
    });
  return terms.length === 0 ? 'Aucune métrique' : terms.join(' + ');
}

/**
 * The metrics a per-day summary shows, in the operator's own order.
 *
 * `derivedOnly` is what the Semaine and Jour grids pass: there, the base
 * metrics are already on screen as the fields you TYPE, so repeating
 * « Départs » as a derived row below the Départs input would be noise. The Mois
 * cell has no entry grid and therefore shows all of them.
 */
export function summaryMetrics(
  registry: MetricRegistry,
  options: { derivedOnly?: boolean } = {},
): MetricDef[] {
  return [...registry.values()]
    .filter((d) => d.inSummary && d.active && (!options.derivedOnly || d.kind !== 'base'))
    .sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key));
}

/**
 * Keys that would become unreachable if `key` were deleted — i.e. every formula
 * metric referencing it, transitively. The editor shows this before a delete;
 * the DB's own FK/trigger is the enforcement.
 */
export function dependentsOf(registry: MetricRegistry, key: string): string[] {
  const out = new Set<string>();
  let frontier = [key];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const [candidateKey, def] of registry) {
      if (def.kind !== 'formula' || out.has(candidateKey) || candidateKey === key) continue;
      const refs = Object.keys(def.formula ?? {});
      if (frontier.some((f) => refs.includes(f))) {
        out.add(candidateKey);
        next.push(candidateKey);
      }
    }
    frontier = next;
  }
  return [...out];
}

/**
 * A provisional `BoardDay` from the counts the desk is CURRENTLY TYPING.
 *
 * The Semaine grid and the Jour card show their derived figures live — typing a
 * departure moves the load immediately, before anything is saved. Evaluating a
 * summary metric against the STORED row would freeze those figures until save,
 * silently turning a live readout into a stale one, so the draft counts are
 * assembled into a day-shaped value the evaluator can read.
 *
 * The two derived-on-save fields are recomputed here by the same rules the
 * database applies (`stayovers = occupancy − arrivals`, `covers = halfBoards +
 * diners`), so the draft and the saved row cannot disagree about them. Since 0146
 * `covers` carries a third term, `dinersHotel` — a draft that omitted it would
 * read low by exactly the hotel's à-la-carte diners while the desk typed.
 */
export function draftBoardDay(input: {
  date: string;
  departures: number;
  arrivals: number;
  /** Null when the occupancy chain is not seeded — stayovers are then unknown too. */
  occupancy: number | null;
  breakfasts: number;
  halfBoards: number;
  diners: number;
  /** 0146 — optional: the desk types neither, so a draft that omits them is
   *  still an honest draft. They default to 0 and `covers` follows suit. */
  packedLunches?: number;
  dinersHotel?: number;
}): BoardDay {
  const stayovers = input.occupancy === null ? 0 : Math.max(0, input.occupancy - input.arrivals);
  return {
    date: input.date,
    departures: input.departures,
    arrivals: input.arrivals,
    stayovers,
    occupancy: input.occupancy,
    breakfasts: input.breakfasts,
    packedLunches: input.packedLunches ?? 0,
    halfBoards: input.halfBoards,
    dinersHotel: input.dinersHotel ?? 0,
    // Fed and read-only, so a draft has nothing to say about it.
    dinersExternalBooked: 0,
    diners: input.diners,
    covers: input.halfBoards + (input.dinersHotel ?? 0) + input.diners,
    note: null,
    updatedAt: '',
    // The desk does not type these — they are feed-owned (0129) — so a DRAFT
    // has nothing to say about them. Null, not 0: this helper exists to keep a
    // live readout honest while typing, and inventing zero guests would be the
    // precise opposite of that.
    guestsAdults: null,
    guestsChildren: null,
    arrivalsGuests: null,
    departuresGuests: null,
  };
}
